import { Flex, Form, Select } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { RingComplianceSelectedCount } from 'business/quality-control/pages/ring-compliance-report/components/ring-compliance-selected-count';
import { useFilterOptions } from 'business/quality-control/pages/ring-compliance-report/hooks/use-filter-options';
import {
  defaultFilters,
  useResetRingComplianceReportFilters,
  useRingComplianceReportFilters,
  useRingComplianceReportFiltersFormName,
} from 'business/quality-control/pages/ring-compliance-report/providers/filters';
import { RingComplianceReportFilterData } from 'business/quality-control/pages/ring-compliance-report/types';
import { FilterData } from 'technical/filter-data';
import { useMediaType } from 'technical/media/hooks';
import Button from 'ui/button';
import { SelectedItemsFooter } from 'ui/filter-footer';
import { DateRangePicker } from 'ui/form/date-range-picker';
import NumberRangeInput from 'ui/form/number-range-input';

import styles from './index.module.scss';

export const RingComplianceFilters = ({
  filterData,
}: {
  filterData?: FilterData<RingComplianceReportFilterData>;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { isMobile } = useMediaType();

  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const showValidationStep =
    currentConstructionSite.isQualityValidatorActivated;

  const formName = useRingComplianceReportFiltersFormName();
  const filters = useRingComplianceReportFilters();
  const resetFilters = useResetRingComplianceReportFilters();

  const { statusOptions } = useFilterOptions(showValidationStep);

  return (
    <>
      <Form layout="inline" form={form} name={formName} initialValues={filters}>
        <Form.Item
          name="ringNumberRange"
          label={t('input.ringNumber.label')}
          layout="vertical"
        >
          <NumberRangeInput
            leftInputProps={{
              min: 0,
              placeholder: t('input.ringRange.startPlaceholder'),
            }}
            rightInputProps={{
              min: 0,
              placeholder: t('input.ringRange.endPlaceholder'),
            }}
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="complianceDateRange"
          label={t('qualityControl.ringComplianceReport.complianceDate')}
          layout="vertical"
        >
          <DateRangePicker allowClear format="L" />
        </Form.Item>

        {showValidationStep ? (
          <Form.Item
            name="approvalDateRange"
            label={t(
              'qualityControl.ringComplianceReport.approvalOrRefusalDate',
            )}
            layout="vertical"
          >
            <DateRangePicker allowClear format="L" />
          </Form.Item>
        ) : null}

        <Form.Item
          name="status"
          label={t('input.validationStatus.label')}
          layout="vertical"
        >
          <Select
            placeholder={t('input.validationStatus.placeholder')}
            mode="multiple"
            allowClear
            options={statusOptions}
            className={styles.statusSelect}
          />
        </Form.Item>
      </Form>
      {isMobile ? (
        <Flex justify="center">
          <Button
            onClick={() => {
              form.setFieldsValue(defaultFilters);
              resetFilters();
            }}
          >
            {t('common.clearFilter')}
          </Button>
        </Flex>
      ) : null}
      {filterData?.selectedRings ? (
        <SelectedItemsFooter mobile>
          <RingComplianceSelectedCount filterData={filterData} />
        </SelectedItemsFooter>
      ) : null}
    </>
  );
};
